import React, { type ComponentType, Suspense, useMemo } from 'react';

import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { ArticleSearchComponent, HelpObjectSearchComponent } from '@atlassian/article-search';
import {
	ExtensionMode,
	type ProductDependenciesProviderProps,
	RESOURCE_TILES_EXTENSION_TYPE,
} from '@atlassian/company-hub-common';
import { JSM_HELP_EXTENSION_TYPE, type JSMHelpParameters } from '@atlassian/jsm-help-extension';
import { type ResourceTilesParameters } from '@atlassian/knowledge-cards-extension';
import {
	CALLOUT_BANNER_EXTENSION_TYPE,
	type CalloutBannerParametersType,
} from '@atlassian/page-topper-extension';

export interface ProductExtensionHandlersProps {
	contentId: string;
	product: string;
	PropsProvider: ComponentType<ProductDependenciesProviderProps>;
}

const CalloutBannerExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_page-topper-extension" */ '@atlassian/page-topper-extension'
	).then((mod) => ({ default: mod.CalloutBannerExtension })),
);

const ResourceTilesExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_knowledge-cards-extension" */ '@atlassian/knowledge-cards-extension'
	).then((mod) => ({ default: mod.ResourceTilesExtension })),
);

const JSMHelpExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_jsm-help-extension" */ '@atlassian/jsm-help-extension'
	).then((mod) => ({ default: mod.JSMHelpExtension })),
);

const getCSMExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
): ExtensionHandlers => {
	const handlers: ExtensionHandlers = {};

	if (!contentId) {
		return handlers;
	}

	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
		CustomSearchComponent: ArticleSearchComponent,
	};

	// Callout banner Extension Handler
	handlers[CALLOUT_BANNER_EXTENSION_TYPE] = (
		extension: ExtensionParams<CalloutBannerParametersType>,
	) =>
		<Suspense fallback={<UFOLoadHold name="page-topper-extension-loader" />}>
			<CalloutBannerExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>

	// Resource Tiles Extension Handler
	handlers[RESOURCE_TILES_EXTENSION_TYPE] = (
		extension: ExtensionParams<ResourceTilesParameters>,
	) =>
		<Suspense fallback={<UFOLoadHold name="resource-tiles-extension-loader" />}>
			<ResourceTilesExtensionLazy
				{...defaultExtensionHandlerProps}
				extensionNode={extension}
				isInEditor={false}
			/>
		</Suspense>

	return handlers;
};

const getJSMExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
): ExtensionHandlers => {
	const handlers: ExtensionHandlers = {};

	if (!contentId) {
		return handlers;
	}

	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
		CustomSearchComponent: HelpObjectSearchComponent,
	};

	handlers[CALLOUT_BANNER_EXTENSION_TYPE] = (
		extension: ExtensionParams<CalloutBannerParametersType>,
	) =>
		<Suspense fallback={<UFOLoadHold name="page-topper-extension-loader" />}>
			<CalloutBannerExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>

	// JSM Help Extension Handler
	handlers[JSM_HELP_EXTENSION_TYPE] = (extension: ExtensionParams<JSMHelpParameters>) =>
		<Suspense fallback={<UFOLoadHold name="jsm-help-extension-loader" />}>
			<JSMHelpExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	return handlers;
};

export const useProductExtensionHandlers = (
	props: ProductExtensionHandlersProps,
): ExtensionHandlers<any> => {
	const { contentId, PropsProvider, product } = props;

	const extensionHandlers: ExtensionHandlers = useMemo(() => {
		if (product === 'CUSTOMER_SERVICE') {
			return getCSMExtensionHandlers(contentId, PropsProvider);
		}

		// Future products can be added here with:
		if (product === 'HELP_HUB' && fg('enable-jsm-in-hubs')) {
			return getJSMExtensionHandlers(contentId, PropsProvider);
		}

		return {};
	}, [product, contentId, PropsProvider]);

	return extensionHandlers;
};
